/**
 * Off-main-thread post-processing for the offline FX renderer.
 *
 * After an OfflineAudioContext render there are two heavy per-sample jobs:
 *   1. 16-bit WAV encoding of the rendered audio (tens of millions of
 *      DataView writes for a long track), and
 *   2. the estimated gain-reduction envelope for the GR meter (a scan of
 *      every sample of the decoded source).
 *
 * Running these on the main thread blocks the UI for seconds on older
 * iPhones (observed as the "main thread busy after render" stalls in
 * HYBRID_AUDIO_ROUTING_PLAN.md). This module runs both inside a Web Worker,
 * transferring the channel buffers in and the WAV buffer out (zero-copy).
 *
 * The worker is built from the *serialized source* of the self-contained
 * core functions in wavEncoder.js / grEnvelope.js (no separate worker build
 * needed under Vue CLI). A one-time self-test verifies the serialized
 * worker actually computes before real jobs are trusted to it; any failure
 * anywhere falls back to the main-thread implementation, preserving the
 * exact previous behavior.
 */

import { encodeWavFromChannels } from './wavEncoder';
import {
  staticReductionDb,
  computeGainReductionEnvelopeFromChannels
} from './grEnvelope';

const WORKER_JOB_TIMEOUT_MS = 60000; // generous: long tracks on slow devices
const WORKER_SELFTEST_TIMEOUT_MS = 3000;

let workerBlobUrl = null;
let workerHealthPromise = null; // Promise<boolean>

function environmentSupportsWorker() {
  return (
    typeof Worker === 'function' &&
    typeof Blob === 'function' &&
    typeof URL !== 'undefined' &&
    typeof URL.createObjectURL === 'function' &&
    // Function names survive as-is or consistently mangled; empty names mean
    // we cannot reconstruct the internal call graph — use the main thread.
    !!staticReductionDb.name &&
    !!computeGainReductionEnvelopeFromChannels.name &&
    !!encodeWavFromChannels.name
  );
}

function buildWorkerUrl() {
  if (workerBlobUrl) return workerBlobUrl;

  // Bind each serialized function to the name its (possibly minified) call
  // sites use — Function.prototype.name matches the mangled identifier.
  const define = (fn) => 'var ' + fn.name + ' = (' + fn.toString() + ');\n';

  // Glue-local identifiers are __pcp-prefixed so they can never collide
  // with (possibly single-letter) minified function names.
  const source =
    define(staticReductionDb) +
    define(computeGainReductionEnvelopeFromChannels) +
    define(encodeWavFromChannels) +
    'self.onmessage = function (__pcpEvent) {\n' +
    '  var __pcpData = __pcpEvent.data;\n' +
    '  try {\n' +
    '    if (__pcpData.selfTest) {\n' +
    '      var __pcpTestWav = ' + encodeWavFromChannels.name + '([new Float32Array(4)], 8000, 4);\n' +
    '      var __pcpTestEnv = ' + computeGainReductionEnvelopeFromChannels.name +
    '([new Float32Array(4)], 8000, 4, { threshold: -24, ratio: 4, attack: 30, release: 100 }, 20);\n' +
    '      self.postMessage({ id: __pcpData.id, ok: __pcpTestWav.byteLength === 44 + 8 && __pcpTestEnv.values.length >= 1 });\n' +
    '      return;\n' +
    '    }\n' +
    '    var __pcpWavBuffer = ' + encodeWavFromChannels.name +
    '(__pcpData.renderedChannels, __pcpData.renderedSampleRate, __pcpData.renderedFrames);\n' +
    '    var __pcpEnvelope = null;\n' +
    '    if (__pcpData.decodedChannels) {\n' +
    '      __pcpEnvelope = ' + computeGainReductionEnvelopeFromChannels.name +
    '(__pcpData.decodedChannels, __pcpData.decodedSampleRate, __pcpData.decodedFrames, __pcpData.compressorSettings, __pcpData.envelopeRate);\n' +
    '    }\n' +
    '    var __pcpTransfers = [__pcpWavBuffer];\n' +
    '    if (__pcpEnvelope) __pcpTransfers.push(__pcpEnvelope.values.buffer);\n' +
    '    self.postMessage({ id: __pcpData.id, wavBuffer: __pcpWavBuffer, envelope: __pcpEnvelope }, __pcpTransfers);\n' +
    '  } catch (__pcpErr) {\n' +
    '    self.postMessage({ id: __pcpData.id, error: String(__pcpErr && __pcpErr.message ? __pcpErr.message : __pcpErr) });\n' +
    '  }\n' +
    '};\n';

  workerBlobUrl = URL.createObjectURL(
    new Blob([source], { type: 'application/javascript' })
  );
  return workerBlobUrl;
}

function runWorkerJob(payload, transfers, timeoutMs) {
  return new Promise((resolve, reject) => {
    let worker;
    try {
      worker = new Worker(buildWorkerUrl());
    } catch (err) {
      reject(err);
      return;
    }

    let settled = false;
    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      try {
        worker.terminate();
      } catch (e) {
        // ignore
      }
      fn(value);
    };

    const timer = setTimeout(
      () => finish(reject, new Error('fx worker timed out')),
      timeoutMs
    );

    worker.onmessage = (e) => {
      if (e.data && e.data.error) {
        finish(reject, new Error(e.data.error));
      } else {
        finish(resolve, e.data);
      }
    };
    worker.onerror = (err) => {
      finish(reject, err instanceof Error ? err : new Error('fx worker error'));
    };

    try {
      worker.postMessage(payload, transfers);
    } catch (err) {
      // Transfer never happened (postMessage is atomic on failure) — the
      // caller can still safely fall back to the main-thread path.
      finish(reject, err);
    }
  });
}

/**
 * One-time health check of the serialized worker (guards against minifier
 * transformations breaking the reconstructed call graph).
 * @returns {Promise<boolean>}
 */
function workerIsHealthy() {
  if (!environmentSupportsWorker()) return Promise.resolve(false);
  if (!workerHealthPromise) {
    workerHealthPromise = runWorkerJob({ selfTest: true, id: 0 }, [], WORKER_SELFTEST_TIMEOUT_MS)
      .then((result) => !!(result && result.ok))
      .catch(() => false);
  }
  return workerHealthPromise;
}

function extractChannels(audioBuffer) {
  const channels = [];
  for (let ch = 0; ch < audioBuffer.numberOfChannels; ch++) {
    channels.push(audioBuffer.getChannelData(ch));
  }
  return channels;
}

function mainThreadPostProcess(rendered, decoded, compressorSettings, envelopeRate) {
  const wavBuffer = encodeWavFromChannels(
    extractChannels(rendered),
    rendered.sampleRate,
    rendered.length
  );
  let envelope = null;
  if (decoded) {
    envelope = computeGainReductionEnvelopeFromChannels(
      extractChannels(decoded),
      decoded.sampleRate,
      decoded.length,
      compressorSettings,
      envelopeRate
    );
  }
  return { wavBuffer, envelope };
}

/**
 * Encode the rendered buffer to WAV and (optionally) compute the GR meter
 * envelope from the decoded source — in a Worker when possible, on the main
 * thread otherwise.
 *
 * @param {AudioBuffer} rendered - Output of the offline render
 * @param {AudioBuffer|null} decoded - Decoded source (null to skip envelope)
 * @param {object} compressorSettings - { threshold, ratio, attack, release }
 * @param {number} [envelopeRate]
 * @returns {Promise<{wavBuffer: ArrayBuffer, envelope: {rate:number, values:Float32Array}|null}>}
 */
export async function postProcessRender(rendered, decoded, compressorSettings, envelopeRate = 20) {
  const healthy = await workerIsHealthy();
  if (!healthy) {
    return mainThreadPostProcess(rendered, decoded, compressorSettings, envelopeRate);
  }

  // Extract channel arrays BEFORE attempting the worker: if postMessage
  // throws, nothing was transferred and the same arrays feed the fallback.
  const renderedChannels = extractChannels(rendered);
  const decodedChannels = decoded ? extractChannels(decoded) : null;

  const payload = {
    id: 1,
    renderedChannels,
    renderedSampleRate: rendered.sampleRate,
    renderedFrames: rendered.length,
    decodedChannels,
    decodedSampleRate: decoded ? decoded.sampleRate : 0,
    decodedFrames: decoded ? decoded.length : 0,
    compressorSettings,
    envelopeRate
  };

  const transfers = [];
  const collectTransfer = (arr) => {
    // Each channel may share or own its ArrayBuffer; transfer each once.
    if (arr && arr.buffer && transfers.indexOf(arr.buffer) === -1) {
      transfers.push(arr.buffer);
    }
  };

  let transferable = true;
  try {
    renderedChannels.forEach(collectTransfer);
    if (decodedChannels) decodedChannels.forEach(collectTransfer);
  } catch (e) {
    transferable = false;
  }

  try {
    const result = await runWorkerJob(
      payload,
      transferable ? transfers : [],
      WORKER_JOB_TIMEOUT_MS
    );
    return { wavBuffer: result.wavBuffer, envelope: result.envelope || null };
  } catch (err) {
    // If the transfer already detached the buffers we cannot recompute on
    // the main thread; the caller degrades to clean (no-FX) playback.
    const detached =
      transferable &&
      renderedChannels.length > 0 &&
      renderedChannels[0].buffer &&
      renderedChannels[0].buffer.byteLength === 0;
    if (detached) {
      throw err;
    }
    return mainThreadPostProcess(rendered, decoded, compressorSettings, envelopeRate);
  }
}
